import { resolveId } from '@medplum/core'
import type { Patient, Reference } from '@medplum/fhirtypes'
import { IconArrowDown, IconBus, IconLoader2 } from '@tabler/icons-react'
import { type JSX, type ReactNode, useState } from 'react'
import { HoverCard } from '#src/components/core/hover-card/HoverCard.tsx'
import { HoverCardContent } from '#src/components/core/hover-card/HoverCardContent.tsx'
import { HoverCardTrigger } from '#src/components/core/hover-card/HoverCardTrigger.tsx'
import { useTransportManifest } from '#src/hooks/useTransportManifest.ts'

interface TransportationTripsHoverCardProps {
  patientRef: Reference<Patient> | undefined
  /** Appointment date as ISO string or Date object */
  appointmentDate: Date | string | undefined
  children: ReactNode
}

export function TransportationTripsHoverCard({
  patientRef,
  appointmentDate: appointmentDateProp,
  children,
}: TransportationTripsHoverCardProps): JSX.Element {
  const patientId = resolveId(patientRef)
  const [hasBeenOpened, setHasBeenOpened] = useState(false)

  const appointmentDate =
    typeof appointmentDateProp === 'string'
      ? new Date(appointmentDateProp)
      : appointmentDateProp

  const {
    data: trips,
    isLoading,
    isError,
    error,
  } = useTransportManifest(appointmentDate, patientId, {
    enabled: hasBeenOpened,
  })

  const regularTrips =
    trips?.filter((t) => !t.jobId.includes('-DELIVERY')) ?? []

  const locations: { time: string; address: string }[] = []
  for (const trip of regularTrips) {
    if (locations.length === 0) {
      locations.push({
        time: trip.scheduledPickupTime || '',
        address: formatAddress(trip.originAddress, trip.originCity),
      })
    }
    locations.push({
      time: trip.appointmentTime || trip.scheduledPickupTime || '',
      address: formatAddress(trip.destinationAddress, trip.destinationCity),
    })
  }

  return (
    <HoverCard
      openDelay={200}
      closeDelay={100}
      onOpenChange={(open) => {
        if (open) {
          setHasBeenOpened(true)
        }
      }}
    >
      <HoverCardTrigger asChild>{children}</HoverCardTrigger>
      <HoverCardContent className="w-72 p-3">
        {isLoading ? (
          <div className="flex items-center justify-center py-4">
            <IconLoader2
              size={20}
              className="animate-spin text-muted-foreground"
            />
          </div>
        ) : isError ? (
          <div className="py-2 text-center text-sm text-destructive">
            Failed to load transport trips. {error?.message}
          </div>
        ) : regularTrips.length === 0 ? (
          <div className="py-2 text-center text-sm text-muted-foreground">
            No transport trips for this day
          </div>
        ) : (
          <div className="flex flex-col gap-1">
            {locations.map((loc, index) => (
              // oxlint-disable-next-line react/no-array-index-key -- stable order, no reordering
              <div key={index}>
                <LocationPuck time={loc.time} address={loc.address} />
                {index < locations.length - 1 && <TripArrow />}
              </div>
            ))}
          </div>
        )}
      </HoverCardContent>
    </HoverCard>
  )
}

function formatAddress(street: string, city: string): string {
  if (street && city) {
    return `${street}, ${city}`
  }
  return street || city || 'Unknown'
}

function TripArrow(): JSX.Element {
  return (
    <div className="ml-1 flex items-center gap-1 px-2 py-0.5 text-muted-foreground">
      <IconArrowDown size={12} />
      <IconBus size={12} />
    </div>
  )
}

interface LocationPuckProps {
  time: string
  address: string
}

function LocationPuck({ time, address }: LocationPuckProps): JSX.Element {
  return (
    <div className="rounded-md border bg-muted/50 p-2">
      <div className="text-sm font-medium">{time || 'TBD'}</div>
      <div className="truncate text-xs text-muted-foreground">{address}</div>
    </div>
  )
}
